import { watch, watchSubscriptions } from "~/state/util";
import { Unsubscribe } from "redux";
import { IPosition, ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { store } from "~/state/store";
import { selectActiveSubscription, selectSubscriptions } from "~/state/share-position/selectors";
import { filterSubscriptions, routeParts2SMapCoords } from "~/share-position2/utils";
import { addListener, removeListener } from "~/lib/signals";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { WASM_ZINDEX } from "./const";

interface IMapSubscription {
	wasmMarker: WasmStoreMarker;
}

/** zoom, na který se nastaví mapa při aktivaci sledování, které nemá geometrii a tedy se nedělá bestfit */
//const NO_GEOMETRY_ZOOM = 20;

function createMarker(subscription: ISubscriptionWithDescription, position: IPosition, optsArg?: { radius?: number; order?: number; }) {
	const opts = {
		radius: 0,
		order: 0,
		...optsArg,
	};
	const wasmMarker = new WasmStoreMarker({
		type: subscription.sharingType === "device" ? "position-share-device" : "azimuth" in subscription.position ? "position-share-image-arrow" : "position-share-image",
		lon: position.lon,
		lat: position.lat,
		visual: "deactive",
		angle: "azimuth" in subscription.position
			? subscription.position.azimuth
			: 0,
		zIndex: WASM_ZINDEX.marker.sharePosition,
		callback: "click-hover",
		customData: {
			url: subscription.user.avatarUrl,
			...opts,
		},
	});

	return wasmMarker;
}

export default class WasmSharePosition {
	#storeUnsubscribeSharePosition: Unsubscribe;

	#storeUnsubscribeSharePositionActive: Unsubscribe;

	#storeUnsubscribeSharePositionHover: Unsubscribe;

	#subscriptions: {
		[key: string]: IMapSubscription;
	};

	#sharepositionActivatedListener: string;

	#sharepositionDeactivatedListener: string;

	#wasmMarkers: Array<WasmStoreMarker>;

	constructor() {
		this.#subscriptions = {};
		this.#wasmMarkers = [];

		// precteme napoprve ze store
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);
		const activeSubsciption = selectActiveSubscription(storeState);

		this.#sharePositionVisualize(subscriptions, activeSubsciption, activeSubsciption);

		// nahodime sledovani stavu ve store
		const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);
		const wtSharePositionActiveSubscription = watch<string>(store.getState, "sharePosition.activeSubscription");
		const wtSharePositionHoverSubscription = watch<string>(store.getState, "sharePosition.hoverSubscription");

		this.#storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(newSubscriptions => {
			const activeSub = selectActiveSubscription(store.getState());

			this.#sharePositionVisualize(newSubscriptions, activeSub, activeSub);
		}));
		this.#storeUnsubscribeSharePositionActive = store.subscribe(wtSharePositionActiveSubscription((activeSub, oldActiveSub) => {
			const subs = selectSubscriptions(store.getState());

			this.#sharePositionVisualize(subs, activeSub, oldActiveSub);
		}));
		this.#storeUnsubscribeSharePositionHover = store.subscribe(wtSharePositionHoverSubscription((hoverSub, oldHoverSub) => {
			if (oldHoverSub) {
				this.#subscriptions[oldHoverSub]?.wasmMarker.update({
					visual: "deactive",
				});
				//this.#subscriptions[oldHoverSub]?.avatar.removeCard();
			}

			if (hoverSub) {
				this.#subscriptions[oldHoverSub]?.wasmMarker.update({
					visual: "standard",
				});
				//this.#subscriptions[hoverSub]?.avatar.showCard();
			}
		}));

		this.#sharepositionActivatedListener = addListener(this, "shareposition-activated", "sharepositionActivated");
		this.#sharepositionDeactivatedListener = addListener(this, "shareposition-deactivated", "sharepositionDeactivated");
	}

	destroy() {
		// zruseni sledovani pozice
		this.#storeUnsubscribeSharePosition();
		this.#storeUnsubscribeSharePositionActive();
		this.#storeUnsubscribeSharePositionHover();
		this.#clearSharePosition();

		removeListener(this.#sharepositionActivatedListener);
		removeListener(this.#sharepositionDeactivatedListener);
	}

	#sharePositionVisualize(subscriptions: Array<ISubscriptionWithDescription>, activeId: string, oldActiveId: string) {
		const filteredSubscriptions = filterSubscriptions(subscriptions);

		// pokud je nove aktivni, schovame geometrii toho stareho, pokud byla videt
		if (activeId !== oldActiveId) {
			if (oldActiveId in this.#subscriptions) {
				const { wasmMarker } = this.#subscriptions[oldActiveId];
				const oldData = wasmMarker.data;

				wasmMarker.destroy();

				const newMarker = new WasmStoreMarker({
					...oldData,
					customData: {
						...oldData.customData,
						// smazeme radius - geometrii
						radius: 0,
					},
				});

				this.#subscriptions[oldActiveId].wasmMarker = newMarker;
			}
		}

		// aktualizujeme, co je videt a co uz zahazujeme
		if (filteredSubscriptions.length) {
			const newSubscriptions: { [key: string]: IMapSubscription; } = {};

			filteredSubscriptions.forEach((subscription, subscriptionIndex) => {
				// mame takovou subscription uz ted? tak jen updatujeme jeji data a poradi ve vypisu
				if (subscription.subscriptionId in this.#subscriptions) {
					const { wasmMarker } = this.#subscriptions[subscription.subscriptionId];

					// geometrie
					const [coordsGone, coordsToGo] = routeParts2SMapCoords(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0);

					// avatar
					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords.toWGS84()[0] : subscription.position?.lon || 0;
					const lat = useGeometryCoords ? geometryCoords.toWGS84()[1] : subscription.position?.lat || 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};

					// pro zmenu indexu - musime udelat novy marker todor
					//avatar.setIndex(subscriptionIndex);

					// todor
					wasmMarker.update({
						lon: position.lon,
						lat: position.lat,
						visual: activeId === subscription.subscriptionId ? "standard" : "deactive",
						..."azimuth" in position
							? {
								angle: position.azimuth,
							}
							: {},
					});

					// dame si item do noveho objektu a ze stareho odstranime
					newSubscriptions[subscription.subscriptionId] = this.#subscriptions[subscription.subscriptionId];
					delete this.#subscriptions[subscription.subscriptionId];
				// je to nova vec, vyrobime ji
				} else {
					// geometrie
					const [coordsGone, coordsToGo] = routeParts2SMapCoords(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0);

					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords.toWGS84()[0] : subscription.position?.lon || 0;
					const lat = useGeometryCoords ? geometryCoords.toWGS84()[1] : subscription.position?.lat || 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};
					const wasmMarker = createMarker(subscription, position, {
						order: subscriptionIndex + 1,
						radius: "accuracy" in subscription.position
							? subscription.position.accuracy
							: 0,
					});

					this.#wasmMarkers.push(wasmMarker);

					// zapamatujeme si vsechno pro budouci uziti
					newSubscriptions[subscription.subscriptionId] = { wasmMarker };
				}
			});

			// co zustalo v this.#subscriptions, to uz ve skutecnosti nechceme
			for (const subscriptionId in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
					this.#destroySubscription(this.#subscriptions[subscriptionId]);
				}
			}

			// nove aktualni si zapamatujeme
			this.#subscriptions = newSubscriptions;
		} else {
			// zrusime vsechno, co jsme mozna dosud ukazovali
			for (const subscriptionId in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
					this.#destroySubscription(this.#subscriptions[subscriptionId]);
				}
			}

			this.#subscriptions = {};
		}

		// novou aktivni dostaneme do popredi todor if (activeId in this.#subscriptions) {

		// pokud je nove aktivni, ukazeme jeji geometrii
		/*
		if (activeId !== oldActiveId) {
			if (activeId in this.#subscriptions) {
				const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[activeId];

				try {
					this.#geometryLayer.addGeometry(geometryGone);
					this.#geometryLayer.addGeometry(geometryToGo);
					start && this.#markerLayer.addMarker(start);
					destination && this.#markerLayer.addMarker(destination);

					// udelame bestfit
					const sceneCoords = [...geometryGone.getCoords(), ...geometryToGo.getCoords()].map(coord => {
						const [lon, lat] = coord.toWGS84();

						return Scene.coordsFromWGS84(lon, lat);
					});
					const centerZoom = sceneCoords.length ? Scene.computeCenterZoom(sceneCoords, true) : [avatar.getCoords(), NO_GEOMETRY_ZOOM];
					const [longitude, latitude] = centerZoom[0].toWGS84();
					const futureZoom = centerZoom[1] === Infinity || centerZoom[1] === -Infinity ? NO_GEOMETRY_ZOOM : centerZoom[1];
					const { zoom } = store.getState().map;

					store.dispatch(setCoordinates({
						longitude,
						latitude,
						zoom: futureZoom,
						animate: !CONFIG.inFrame && futureZoom >= zoom - 1 && futureZoom <= zoom + 1,
					}));
					Mapy.getComponent("sharePosition").activate();
					Mapy.getComponent("history").save();
				} catch (ex) {
					//
				}
			}
		}*/
	}

	#clearSharePosition() {
		for (const subscriptionId in this.#subscriptions) {
			if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
				this.#destroySubscription(this.#subscriptions[subscriptionId]);
			}
		}

		this.#subscriptions = {};
	}

	#destroySubscription(subscription: IMapSubscription) {
		const { wasmMarker } = subscription;

		wasmMarker.destroy();

		const ind = this.#wasmMarkers.indexOf(wasmMarker);

		if (ind !== -1) {
			this.#wasmMarkers.splice(ind, 1);
		}
	}

	/* eslint-disable-next-line */
	sharepositionActivated() {
		/*
		const { activeSubscription } = store.getState().sharePosition;

		for (const subscriptionId in this.#subscriptions) {
			if (subscriptionId !== activeSubscription && Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
				const { avatar, accuracy } = this.#subscriptions[subscriptionId];

				this.#accuracyLayer.addMarker(accuracy);
				this.#avatarLayer.addMarker(avatar);
			}
		}

		if (activeSubscription && activeSubscription in this.#subscriptions) {
			const { avatar, accuracy } = this.#subscriptions[activeSubscription];

			this.#accuracyLayer.addMarker(accuracy);
			this.#avatarLayer.addMarker(avatar);
		}
		*/
	}

	/* eslint-disable-next-line */
	sharepositionDeactivated() {
		/*
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);

		subscriptions.forEach(subscription => {
			if (!subscription.attributes.showOnMap && subscription.subscriptionId in this.#subscriptions) {
				const { avatar, accuracy } = this.#subscriptions[subscription.subscriptionId];

				this.#accuracyLayer.removeMarker(accuracy);
				this.#avatarLayer.removeMarker(avatar);
			}
		});
		*/
	}
}
